/**
 * CloudFrugal brand mark — three descending clouds (Hand Holds Cloud direction).
 * Big cloud = forest (#0F3D2E), mid = sage (#4E7D5E), small = gold (#E8B84C).
 * Represents cost stepping down to savings.
 */

interface MarkProps {
  size?: number;
  /** Override cloud colors for reversed/monochrome contexts */
  primaryColor?: string;
  midColor?: string;
  accentColor?: string;
}

export function CloudMark({
  size = 40,
  primaryColor = '#0F3D2E',
  midColor = '#4E7D5E',
  accentColor = '#E8B84C',
}: MarkProps) {
  const h = Math.round(size * 0.5);
  return (
    <svg width={size} height={h} viewBox="0 0 140 70" fill="none" aria-hidden="true">
      {/* Big cloud — forest green, left */}
      <path
        fill={primaryColor}
        d="M 20 60 C 7 60, 0 50, 0 38 C 0 28, 8 20, 18 20 C 20 8, 30 2, 42 2 C 56 2, 66 10, 68 22 C 80 22, 88 30, 88 42 C 88 52, 80 60, 68 60 Z"
      />
      {/* Mid cloud — sage, centre */}
      <path
        fill={midColor}
        d="M 95 62 C 86 62, 80 55, 80 46 C 80 38, 86 32, 94 32 C 96 22, 104 16, 112 16 C 122 16, 130 24, 130 34 C 134 34, 138 38, 138 44 C 138 54, 130 62, 120 62 Z"
        transform="translate(-14 0)"
      />
      {/* Small cloud — gold, right */}
      <path
        fill={accentColor}
        d="M 125 64 C 120 64, 117 60, 117 54 C 117 48, 121 45, 126 45 C 127 38, 132 33, 139 33 C 146 33, 151 38, 151 45 C 154 45, 156 48, 156 52 C 156 58, 152 64, 146 64 Z"
        transform="translate(-16 0)"
      />
    </svg>
  );
}

interface WordmarkProps {
  size?: number;
  /** Light mode (default): "Cloud" forest, "Frugal" faded forest */
  variant?: 'light' | 'dark' | 'onAccent';
}

export function Wordmark({ size = 24, variant = 'light' }: WordmarkProps) {
  const cloudColor =
    variant === 'dark' ? '#ffffff' : variant === 'onAccent' ? '#0F3D2E' : '#0F3D2E';
  const frugalColor =
    variant === 'dark' ? '#E8B84C' : variant === 'onAccent' ? 'rgba(15,61,46,0.55)' : 'rgba(15,61,46,0.55)';

  return (
    <span
      style={{
        fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
        fontSize: size,
        lineHeight: 1,
      }}
    >
      <span style={{ color: cloudColor, fontWeight: 800, letterSpacing: '0.04em' }}>Cloud</span>
      <span style={{ color: frugalColor, fontWeight: 300, letterSpacing: '-0.06em' }}>Frugal</span>
    </span>
  );
}

/** Full horizontal lockup: mark + wordmark */
interface LockupProps {
  markSize?: number;
  wordmarkSize?: number;
  gap?: number;
  variant?: WordmarkProps['variant'];
  markPrimaryColor?: string;
  markMidColor?: string;
  markAccentColor?: string;
}

export function CloudFrugalLockup({
  markSize = 56,
  wordmarkSize = 28,
  gap = 14,
  variant = 'light',
  markPrimaryColor,
  markMidColor,
  markAccentColor,
}: LockupProps) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap }}>
      <CloudMark
        size={markSize}
        primaryColor={markPrimaryColor}
        midColor={markMidColor}
        accentColor={markAccentColor}
      />
      <Wordmark size={wordmarkSize} variant={variant} />
    </span>
  );
}
